import { z } from "zod/v4";
import { DateUtils } from "../../../primitive-utils";
import { LOCAL_DATE_FORMAT } from "../../../time";
import { REGEX_LOCAL_DATE } from "../base/time-regex";
import { DaySchema, MonthSchema, YearSchema } from "../base/time-schema";
import { LocalDateTime } from "../local-date-time";
const LocalDateSchema = z
    .object({
    ...YearSchema.shape,
    ...MonthSchema.shape,
    ...DaySchema.shape,
})
    .refine(({ year, month, day }) => {
    const date = new Date(year, month - 1, day);
    return (date.getFullYear() === year &&
        date.getMonth() + 1 === month &&
        date.getDate() === day);
}, {
    error: "存在しない日付です。",
})
    .brand("Year")
    .brand("Month")
    .brand("Day");
// ** LocalDate <-> Date 変換系 ** //
/** DateをJST解釈のLocalDateに変換 */
const fromDateJST = (v) => {
    const jstDate = DateUtils.utcToJSTTime(v);
    return fromDate(jstDate);
};
/** LocalDateをJST解釈のDateに変換 */
const toDateJST = (v) => {
    return DateUtils.JSTTimeToUtc(toDate(v));
};
/** その日の0:00(JST)時点のDateを返す */
const atStartOfDayJST = (v) => {
    return DateUtils.startOfDayJST(toDate(v));
};
/** その日の23:59(JST)時点のDateを返す */
const atEndOfDayJST = (v) => {
    return DateUtils.endOfDayJST(toDate(v));
};
/** private. TZは実行環境に依存する */
const toDate = ({ year, month, day }) => {
    return new Date(year, month - 1, day);
};
/** private. TZは実行環境に依存する */
const fromDate = (d) => {
    return LocalDate.of({
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        day: d.getDate(),
    });
};
// ** LocalDate <-> string 変換系 ** //
/** LocalDate -> string */
const format = (v, format = LOCAL_DATE_FORMAT) => {
    return DateUtils.format(toDate(v), format, true);
};
const formatJPEra = (v) => {
    const formatter = new Intl.DateTimeFormat("ja-jp-u-ca-japanese", {
        era: "short",
        year: "numeric", // 2digitに設定すると2桁のみの表示になる。足りなければ0埋め
        month: "numeric",
    });
    const date = toDate(v);
    const result = formatter.format(date);
    return result;
    // return `${era}${year}年${v.month}月${v.day}日`;
};
/**
 * 2020-01-01, 2020/01/01, 2020年01月01日(火) などの形式のLocalDateを扱うえるようにするスキーマ
 */
const LocalDateStringSchema = z
    .string()
    .regex(REGEX_LOCAL_DATE, { message: "日付の形式が不正です。" })
    .transform((val) => {
    const extractRegex = /\d+/g; // 数字を抽出
    const [year, month, day] = val.match(extractRegex)?.map(Number) ?? [];
    return { year, month, day };
});
/** 形式チェックのみで、厳密な日付存在チェックを経ないparse */
const parseSoft = (v) => {
    return LocalDateStringSchema.parse(v);
};
/** string => LocalDate */
const parse = (v) => {
    return LocalDate.schema.parse(parseSoft(v));
};
// ** LocalDateの判定系 ** //
// USAGE: localDateArray.sort(LocalDate.compere);
// 日の昇順 1/1, 1/2, 1/3... で並ぶ
const compare = (a, b) => {
    return toDate(a).getTime() - toDate(b).getTime();
};
// 日の降順 1/3,1/2,1/1... で並ぶ
const compareDesc = (a, b) => {
    return toDate(b).getTime() - toDate(a).getTime();
};
const equals = (a, b) => {
    return toDate(a).getTime() === toDate(b).getTime();
};
const isAfter = (a, b) => {
    return toDate(a).getTime() > toDate(b).getTime();
};
const isAfterOrEqual = (a, b) => {
    return toDate(a).getTime() >= toDate(b).getTime();
};
const isBefore = (a, b) => {
    return toDate(a).getTime() < toDate(b).getTime();
};
const isBeforeOrEqual = (a, b) => {
    return toDate(a).getTime() <= toDate(b).getTime();
};
const countDays = (a, b) => {
    return DateUtils.differenceInDays(toDate(b), toDate(a));
};
// ** LocalDateの操作系 ** //
const startOfMonth = (v) => {
    return LocalDate.of({ ...v, day: 1 });
};
const endOfMonth = (v) => {
    return fromDate(DateUtils.endOfMonth(toDate({ ...v, day: 1 })));
};
const addDays = (v, days) => {
    return fromDate(DateUtils.addDays(toDate(v), days));
};
const subDays = (v, days) => {
    return fromDate(DateUtils.subDays(toDate(v), days));
};
const addMonths = (v, months) => {
    return fromDate(DateUtils.addMonths(toDate(v), months));
};
const subMonths = (v, months) => {
    return fromDate(DateUtils.subMonths(toDate(v), months));
};
const addYears = (v, years) => {
    return fromDate(DateUtils.addYears(toDate(v), years));
};
const subYears = (v, years) => {
    return fromDate(DateUtils.subYears(toDate(v), years));
};
const setDay = (v, dayOfMonth) => {
    return LocalDate.of({ ...v, day: dayOfMonth });
};
const atTime = (v, time) => {
    return LocalDateTime.of({ ...v, ...time });
};
export const LocalDate = {
    schema: LocalDateSchema,
    stringSchema: LocalDateStringSchema,
    TODAY: () => fromDateJST(new Date()),
    of(args) {
        return LocalDate.schema.parse(args);
    },
    // ** LocalDate <-> Date 変換系 ** //
    toDateJST,
    atStartOfDayJST,
    atEndOfDayJST,
    fromDateJST,
    // ** LocalDate <-> string 変換系 ** //
    format,
    parseSoft,
    parse,
    formatJPEra,
    // ** LocalDateの判定系 ** //
    compare,
    compareDesc,
    equals,
    isAfter,
    isAfterOrEqual,
    isBefore,
    isBeforeOrEqual,
    countDays,
    // ** LocalDateの操作系 ** //
    startOfMonth,
    endOfMonth,
    addDays,
    subDays,
    addMonths,
    subMonths,
    addYears,
    subYears,
    setDay,
    atTime,
};
//# sourceMappingURL=local-date.js.map